import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Palette, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { apiError } from "../lib/auth";

const POSITIONS = [["top-left", "Top left"], ["top-right", "Top right"], ["bottom-left", "Bottom left"],
  ["bottom-right", "Bottom right"], ["bottom-center", "Bottom centre"]];

/* How the engine's posts look: a colour theme and the logo stamped on every
   image. Both are written to the engine's brand - the same record the Brands
   page edits - so there is one source of truth. With no brand chosen, the
   first change creates one for the engine. */
export const EngineLook = ({ brandId, brands, workspaceName, onBrandCreated, refreshBrands }) => {
  const [themes, setThemes] = useState([]);
  const [busy, setBusy] = useState("");
  const file = useRef(null);
  const brand = brands.find((b) => b.id === brandId);
  const g = brand?.guidelines || {};
  const colors = g.colors || {};

  useEffect(() => {
    api.get("/brand-presets").then(({ data }) => setThemes(data.presets || [])).catch(() => {});
  }, []);

  // A brand to write to: the engine's own, or a new one made for it.
  const ensureBrand = async (guidelines = {}) => {
    if (brand) return brand;
    const { data } = await api.post("/brands", {
      name: `${workspaceName || "Autopilot"} brand`, category: "", tone: "", instructions: "", color: "#6366F1",
      guidelines,
    });
    await onBrandCreated(data.id);
    return data;
  };

  const save = async (b, guidelines) => {
    const body = { name: b.name, category: b.category || "", tone: b.tone || "", instructions: b.instructions || "",
      color: guidelines.colors?.accent || b.color || "#6366F1", guidelines };
    await api.put(`/brands/${b.id}`, body);
    await refreshBrands();
  };

  const applyTheme = async (id) => {
    setBusy(`theme:${id}`);
    try {
      const { data } = await api.get(`/brand-presets/${id}`);
      // A theme replaces the look - palette, type, image direction - but
      // keeps what is the brand's own: its logo, footer and voice notes.
      const keep = ["logo_base64", "logo_position", "logo_scale", "logo_opacity", "logo_margin", "footer_text"];
      const b = await ensureBrand(data.guidelines);
      const merged = { ...(b.guidelines || {}), ...data.guidelines };
      keep.forEach((k) => { if ((b.guidelines || {})[k] !== undefined) merged[k] = b.guidelines[k]; });
      await save(b, merged);
      toast.success("Theme applied - new posts will use it");
    } catch (err) { toast.error(apiError(err, "Could not apply the theme")); }
    finally { setBusy(""); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("logo");
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/brands/logo", form);
      const b = await ensureBrand();
      await save(b, { ...(b.guidelines || {}), logo_base64: data.logo_base64,
        logo_position: (b.guidelines || {}).logo_position || "top-left" });
      toast.success("Logo added - it's stamped on every new image");
    } catch (err) { toast.error(apiError(err, "Could not use that logo")); }
    finally { setBusy(""); }
  };

  const setLogo = async (patch) => {
    if (!brand) return;
    setBusy("pos");
    try { await save(brand, { ...g, ...patch }); } catch (err) { toast.error(apiError(err, "Could not save")); }
    finally { setBusy(""); }
  };

  const activeTheme = themes.find((t) => t.colors?.accent === colors.accent && t.colors?.background === colors.background)?.id;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-5 space-y-5" data-testid="engine-look">
      <div className="flex items-center gap-2">
        <Palette className="w-4 h-4 text-indigo-300" />
        <h4 className="text-sm font-semibold text-slate-100">Look</h4>
        <span className="text-[11px] text-slate-500">{brand ? `Saved to ${brand.name}` : "Creates a brand for the engine on first change"}</span>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-400 mb-2">Colour theme</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {themes.map((t) => {
            const c = t.colors || {};
            const on = activeTheme === t.id;
            return (
              <button key={t.id} onClick={() => applyTheme(t.id)} disabled={!!busy} title={t.description}
                className={`text-left rounded-xl border p-2.5 transition-colors ${on ? "border-indigo-400/70 bg-indigo-500/10" : "border-slate-800 hover:border-slate-700"}`}>
                {/* The swatch is the theme itself: its background with its type
                    and accent on it - not a row of loose dots. */}
                <div className="h-14 rounded-lg relative overflow-hidden border border-white/5" style={{ background: c.background }}>
                  <span className="absolute left-2 top-2 text-[13px] font-extrabold" style={{ color: c.text }}>Aa</span>
                  <span className="absolute left-2 bottom-2 h-1.5 w-10 rounded" style={{ background: c.accent }} />
                  <span className="absolute right-2 bottom-2 w-4 h-4 rounded-full" style={{ background: c.accent_soft }} />
                  {busy === `theme:${t.id}` && <Loader2 className="absolute right-2 top-2 w-4 h-4 animate-spin" style={{ color: c.text }} />}
                  {on && <Check className="absolute right-2 top-2 w-4 h-4" style={{ color: c.accent }} />}
                </div>
                <p className="mt-1.5 text-xs font-semibold text-slate-200 truncate">{t.name}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-400 mb-2">Logo on every image</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-20 h-20 rounded-xl border border-slate-800 flex items-center justify-center overflow-hidden"
            style={{ background: colors.background || "#0b0f17" }}>
            {g.logo_base64
              ? <img src={`data:image/png;base64,${g.logo_base64}`} alt="Logo" className="max-w-[80%] max-h-[80%] object-contain" />
              : <span className="text-[10px] text-slate-600">No logo</span>}
          </div>
          <div className="space-y-2">
            <div className="flex gap-2">
              <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={uploadLogo} />
              <button onClick={() => file.current?.click()} disabled={!!busy}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-semibold text-slate-100">
                {busy === "logo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {g.logo_base64 ? "Replace" : "Upload logo"}
              </button>
              {g.logo_base64 && (
                <button onClick={() => setLogo({ logo_base64: "" })} disabled={!!busy} title="Remove logo"
                  className="px-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
            {g.logo_base64 && (
              <div className="flex flex-wrap gap-1.5">
                {POSITIONS.map(([k, l]) => (
                  <button key={k} onClick={() => setLogo({ logo_position: k })} disabled={!!busy}
                    className={`px-2 py-1 rounded-md border text-[11px] ${(g.logo_position || "top-left") === k
                      ? "border-indigo-400/60 bg-indigo-500/10 text-indigo-100" : "border-slate-800 text-slate-400"}`}>{l}</button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500">PNG with a transparent background works best. Placed after rendering, so it's always exact.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
